import { Box, MenuItem, styled, Typography } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { DynamicTypography } from '../DynamicTextTypography'

// Loose typing on purpose: when used as a value for Autocomplete's renderOption
// prop, TS infers the Autocomplete generic from this signature. Strict types
// here would narrow the entire Autocomplete and break the surrounding code's
// access to product-specific fields. The option is always a product-shaped
// object at runtime.
interface ProductOptionLike {
  id?: any
  description?: string | null
  number?: string | null
}

/**
 * Shared renderer for product Autocomplete options. Renders the description
 * as the primary line and the product number as a monospaced caption beneath,
 * left-aligned, wrapping long descriptions instead of clipping.
 */
export const renderProductOption = (liProps: React.HTMLAttributes<HTMLLIElement>, option: any) => (
  <MenuItem
    key={option.id ?? undefined}
    value={option.id ?? undefined}
    {...liProps}
    sx={{
      display: 'flex !important',
      flexDirection: 'column',
      alignItems: 'flex-start !important',
      justifyContent: 'flex-start',
      textAlign: 'left',
      whiteSpace: 'normal',
      width: '100%',
      py: 1,
      px: 1.5,
      gap: 0.25,
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        lineHeight: 1.3,
        color: 'text.primary',
        textAlign: 'left',
        width: '100%',
      }}
    >
      {option.description}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontFamily: 'monospace',
        lineHeight: 1.2,
        textAlign: 'left',
        width: '100%',
      }}
    >
      #{option.number}
    </Typography>
  </MenuItem>
)

/**
 * Shared label formatter for product Autocomplete options. Returns
 * "Description (NUMBER)" so the visible input value and the built-in
 * Autocomplete filter both match on either field.
 */
export const formatProductLabel = (option: any): string => {
  if (!option || typeof option === 'string') return ''
  const o = option as ProductOptionLike
  if (!o.description) return ''
  return o.number ? `${o.description} (${o.number})` : o.description
}

const tokenizeSearchInput = (input: string) => input.toLowerCase().trim().split(/\s+/).filter(Boolean)

const productSearchTokens = (product: ProductOptionLike) =>
  tokenizeSearchInput(`${product.number ?? ''} ${product.description ?? ''}`)

/**
 * Each search word must match a product token in order (substring match within a token,
 * e.g. "24" matches "8x24mm").
 */
export const productMatchesSearch = (product: ProductOptionLike, searchWords: string[]): boolean => {
  if (searchWords.length === 0) return true

  const optionTokens = productSearchTokens(product)
  let tokenIndex = 0

  for (const word of searchWords) {
    while (tokenIndex < optionTokens.length && !optionTokens[tokenIndex].includes(word)) {
      tokenIndex++
    }
    if (tokenIndex >= optionTokens.length) return false
    tokenIndex++
  }

  return true
}

/** Custom filter for product Autocomplete fields (number + description). */
export const filterProductOptions = <T extends ProductOptionLike>(options: T[], state: { inputValue: string }): T[] => {
  const searchWords = tokenizeSearchInput(state.inputValue)
  if (searchWords.length === 0) return options
  return options.filter(option => productMatchesSearch(option, searchWords))
}

interface ProductWithSupplierOptionProps {
  liProps: React.HTMLAttributes<HTMLLIElement>
  option: any
  minWidth?: string
}

export const ProductWithSupplierOption: React.FC<ProductWithSupplierOptionProps> = ({
  liProps,
  option,
  minWidth,
}) => {
  const supplierKey = option.supplier?.id ?? option.supplier?.name
  const { t } = useTranslation('common')

  return (
    <ProductWithSupplierOptionRow {...liProps} key={option.id} minWidth={minWidth}>
      <ProductWithSupplierOptionContent>
        <ProductNumber noWrap variant="body2">
          {option.number}
        </ProductNumber>
        <ProductDescription variant="body2">{option.description}</ProductDescription>
      </ProductWithSupplierOptionContent>
      {option.supplier?.name ? (
        <SupplierBadge>
          <Typography variant="caption" noWrap sx={{ fontSize: '0.6rem', opacity: 0.7 }}>
            {t('supplier')}
          </Typography>
          <SupplierBadgeText text= {option.supplier.name} variant="caption" noWrap />
        </SupplierBadge>
      ) : null}
    </ProductWithSupplierOptionRow>
  )
}

export const renderProductWithSupplierOption = (
  liProps: React.HTMLAttributes<HTMLLIElement>,
  option: any,
) => <ProductWithSupplierOption liProps={liProps} option={option} />

const ProductWithSupplierOptionRow = styled('li', {
  shouldForwardProp: prop => prop !== 'minWidth',
})<{ minWidth?: string }>(({ theme, minWidth }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  width: '100%',
  ...(minWidth ? { minWidth } : {}),
  backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover, &.Mui-focused, &.Mui-selected': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.infoLight),
  },
}))

const ProductWithSupplierOptionContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  minWidth: 0,
  flex: 3,
}))

const ProductNumber = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const ProductDescription = styled(Typography)(({ theme }) => ({
  lineHeight: 1.25,
  fontWeight: 400,
  color: theme.palette.text.secondary,
}))

const SupplierBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'badgeToken',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
}))

const SupplierBadgeText = styled(DynamicTypography)(({ theme }) => ({
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))
